import React from 'react';

import { TextInput, Stack, Group, Button, Title, AutocompleteItem, Checkbox } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import Drawer from 'components/Drawer';
import TaxRateAutoComplete, {
  TaxRateAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/TaxRateAutoComplete';
import MoneyField from 'components/MoneyField';
import { useAuth } from 'context/auth-context';
import useCreateProduct from 'hooks/product/useCreateProduct';
import useProduct from 'hooks/product/useProduct';
import useUpdateProduct from 'hooks/product/useUpdateProduct';
import { ProductWrite } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  name: string;
  price: number;
  taxRate: TaxRateAutoCompleteValue;
  isFavorite: boolean;
}

const DrawerFeesAndMiscProductsForm = ({
  opened,
  productId,
  closeHandler,
  handleResetCart,
  permission = true,
}: {
  opened: boolean;
  productId?: string;
  closeHandler: () => void;
  handleResetCart: () => void;
  permission?: boolean;
}): JSX.Element => {
  const { session } = useAuth();

  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();

  const { data, isLoading, isSuccess } = useProduct(productId);

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      price: 0,
      isFavorite: false,
      taxRate: {
        taxRateId: '',
        value: '',
        label: '',
      },
    },

    validate: {
      name: value => (value.trim().length > 0 ? null : 'Name is required'),
      price: value => (value >= 0 ? null : 'Price must be provided'),
    },

    transformValues: values => ({
      ...values,
      price: Math.round(values.price * 100),
    }),
  });

  const handleTaxRateChange = (item: AutocompleteItem | null): void => {
    const newValue = {
      taxRateId: item ? item.taxRateId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('taxRate', newValue);
  };

  const handleCloseAndReset = (): void => {
    form.reset();
    closeHandler();
  };

  const handleSubmit = (values: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    const payload: ProductWrite & { file: null } = {
      ...values,
      productId: productId || uuidv4(),
      type: 'FEES AND MISCELLANEOUS',
      taxRate: form.values.taxRate.taxRateId || null,
      file: null,
    };

    if (productId) {
      updateMutation.mutate(payload, {
        onError: error => {
          errorNotification(error.message);
        },
        onSuccess: () => {
          successNotification('Fee or miscellaneous product updated');
          handleCloseAndReset();
          handleResetCart();
        },
      });
    } else {
      createMutation.mutate(payload, {
        onError: error => {
          errorNotification(error.message);
        },
        onSuccess: () => {
          successNotification('Fee or miscellaneous product created');
          handleCloseAndReset();
        },
      });
    }
  };

  useShallowEffect(() => {
    if (data?.data && !isLoading && isSuccess && opened) {
      form.setValues({
        name: data.data.name,
        price: (data.data.price || 0) / 100,
        isFavorite: data.data.isFavorite,
        taxRate: {
          taxRateId: data.data?.taxRate?.taxRateId || '',
          value: data.data?.taxRate?.taxRateId || '',
          label: data.data?.taxRate?.name || '',
        },
      });
    }
  }, [data?.data, isLoading, isSuccess, opened]);

  return (
    <Drawer
      title={
        productId ? (
          <Title order={3}>{permission ? 'Edit' : 'View'} Fee or Misc Product</Title>
        ) : (
          <Title order={3}>Create Fee or Misc Product</Title>
        )
      }
      opened={opened}
      onClose={handleCloseAndReset}
      size={650}
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            form='retail-form'
            type='submit'
            loading={createMutation.isLoading || updateMutation.isLoading}
            disabled={!permission || isLoading}
          >
            Save
          </Button>
        </Group>
      }
    >
      <form onSubmit={form.onSubmit(handleSubmit)} id='retail-form'>
        <Group grow align='top' spacing='xl'>
          <Stack>
            <TextInput
              {...form.getInputProps('name')}
              label='Name'
              placeholder='Enter name'
              required
              disabled={!permission}
            />
            <Checkbox
              {...form.getInputProps('isFavorite')}
              checked={form.values?.isFavorite}
              label='Add To Favorites'
              disabled={!permission}
              data-cy='retailFavoriteButton'
            />
            <MoneyField
              country={session?.gym?.country}
              {...form.getInputProps('price')}
              label='Price'
              min={1}
              required
              disabled={!permission}
            />
            <TaxRateAutoComplete
              disabled={!permission}
              label='Tax Rate'
              placeholder='Set tax rate'
              value={form.values.taxRate}
              onChange={handleTaxRateChange}
              error={form.errors.taxRate}
              zIndex={99999}
            />
          </Stack>
        </Group>
      </form>
    </Drawer>
  );
};

export default DrawerFeesAndMiscProductsForm;
